import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { CalendarPlus } from "lucide-react";
import { MAX_GROOMING_BATCH_ITEMS, type GroomingBatchItemView } from "@ttc-spectree/toolbox-contract";
import { Chip, type ChipTone } from "@/components/grooming/grooming-chips";
import { Button } from "@/components/ui/button";
import { useBacklogBriefs } from "@/hooks/queries/use-brief-staleness";
import { groomingKeys, useNextAgenda, useProjectSession, useUndoNextAgenda } from "@/hooks/queries/use-grooming";
import { canOn, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import { retryTransientOnce, workItemKeys } from "@/hooks/queries/use-work-items";
import { useRefetchOnReturn } from "@/hooks/use-refetch-on-return";
import type { GroomingBacklog, GroomingBatch, NextAgendaResult } from "@/lib/api/grooming";
import { workItemsApi, type WorkItemDetail } from "@/lib/api/work-items";
import { formulatorImprovePath } from "@/lib/api/work-item-interview";
import { GROOMING_AGENDA_ERROR_MESSAGES, groomingApiErrorCode } from "@/lib/grooming/error-messages";
import { carryOverDate, formatMeetingDate } from "@/lib/grooming/meeting-dates";
import {
  FIX_LIST_HEADING,
  LEFT_OUT_TEXT,
  batchItemBriefState,
  briefStateTag,
  confidenceWords,
} from "@/lib/grooming/plain-words";
import { showGroomingToast } from "@/lib/grooming/toasts";
import { cn } from "@/lib/utils";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";

/**
 * The Agenda view of a session's run step (epic PI-SPEC-SW-E280, feature
 * PI-SPEC-SW-1403; Claude Design "SpecTree Grooming Desktop", run step
 * Agenda; was E279's agenda view): the same batch read as the meeting.
 * Large type and plain words for screen-sharing. Each row shows Claude's
 * suggestion, or a pending shimmer while it is still to come, and a state
 * chip; choosing a row opens it in the Workspace. Items below the grooming
 * bar sit in the fix list (FIX_LIST_HEADING) with a Formulator link each.
 * Post all lives in the run step's header.
 *
 * After the meeting (PKG-PM-C, UX-PM-13, frame C2 of "PM Package C
 * Mockups.dc.html", feature PI-SPEC-SW-1490): every agenda item is read from
 * ADO (one read per item through `workItemKeys.detail`, the cache Present and
 * the shared agenda page use too; at most 20, the batch cap). A read-only
 * **Team** column shows the points the team typed in ADO ("5 pts") or "No
 * points yet"; a summary card says "5 of 8 sized in ADO · 3 still without
 * points" with the age of the read and Read again, and the view re-reads
 * every item when the window regains focus. A manage-agenda holder gets
 * **Carry over N items** to the agenda for the meeting after this run's,
 * followed by an Undo toast, never a confirm dialog (01-roles.md §1.1). The
 * Toolbox never writes Story Points: the only action here is the next
 * meeting's agenda, and the server re-checks every item, so a stale page can
 * never add a sized or below-bar item. Items that dropped below the bar join
 * the fix list. Claude's size stays visible to everyone with access (stage 1,
 * 03-evaluation.md §7.2).
 */

/**
 * The state chip of an agenda row, in the shared brief words (UX-PM-09).
 * `latest` is the item's latest brief from the cached whole-backlog read
 * (`useBacklogBriefs`); batch reads carry no staleness and a `current` item
 * no brief. The same `batchItemBriefState` as the run list's `rowTag`.
 */
export function agendaState(
  item: GroomingBatchItemView,
  latest: Parameters<typeof batchItemBriefState>[1] = null
): { label: string; tone: ChipTone } {
  switch (item.status) {
    case "briefed":
    case "current": {
      const tag = briefStateTag(batchItemBriefState(item, latest));
      return { label: tag.text, tone: tag.tone };
    }
    case "running":
      return { label: "Running", tone: "red" };
    case "queued":
      return { label: "Queued", tone: "none" };
    case "failed":
      return { label: "Failed", tone: "red" };
    case "skipped":
      return { label: LEFT_OUT_TEXT, tone: "none" };
    case "not-ready":
      return { label: "Needs fixes", tone: "warn" };
  }
}

/** The words under a pending row's shimmer. */
function pendingText(item: GroomingBatchItemView): string {
  switch (item.status) {
    case "running":
      return "Claude is reading the code…";
    case "queued":
      return "Waiting for its turn";
    case "failed":
      return "Couldn't finish this brief";
    default:
      return "No brief yet";
  }
}

// ---------------------------------------------------------------------------
// The team's points, read back from ADO (read only)
// ---------------------------------------------------------------------------

/**
 * The team's points on an item: its live ADO estimate when that is a finite
 * positive number, else null (the backlog's and the server's rule).
 */
export function teamPoints(detail: Pick<WorkItemDetail, "estimate"> | undefined): number | null {
  const estimate = detail?.estimate;
  return typeof estimate === "number" && Number.isFinite(estimate) && estimate > 0 ? estimate : null;
}

/** "5 pts", "1 pt". */
export function pointsText(points: number): string {
  return `${String(points)} ${points === 1 ? "pt" : "pts"}`;
}

/** How much of the meeting is recorded in ADO, from the per-item reads. */
export interface MeetingRecord {
  /** Items with team points in ADO, in agenda order. */
  sized: string[];
  /** Items with no team points that still meet the grooming bar: what Carry over adds. */
  unsized: string[];
  /** Items with no team points that dropped below the bar: they go to the fix list instead. */
  belowBar: string[];
  /** Items not read yet (or whose read failed): counted in neither. */
  unread: string[];
}

/**
 * Sort the agenda's items by what ADO says now. `details` maps an item id to
 * its live read (absent while loading or after a failed read). Pure; exported
 * for tests.
 */
export function meetingRecord(
  items: readonly Pick<GroomingBatchItemView, "itemId">[],
  details: ReadonlyMap<string, Pick<WorkItemDetail, "estimate" | "readiness">>
): MeetingRecord {
  const record: MeetingRecord = { sized: [], unsized: [], belowBar: [], unread: [] };
  for (const { itemId } of items) {
    const detail = details.get(itemId);
    if (!detail) record.unread.push(itemId);
    else if (teamPoints(detail) !== null) record.sized.push(itemId);
    else if (detail.readiness.readyForGrooming) record.unsized.push(itemId);
    else record.belowBar.push(itemId);
  }
  return record;
}

/** "5 of 8 sized in ADO · 3 still without points"; "8 of 8 sized in ADO" when none are left. */
export function recordHeadline(record: Pick<MeetingRecord, "sized" | "unsized">, total: number): string {
  const sized = `${String(record.sized.length)} of ${String(total)} sized in ADO`;
  return record.unsized.length > 0 ? `${sized} · ${String(record.unsized.length)} still without points` : sized;
}

/** The age of a read: "just now", "1 minute ago", "12 minutes ago", "2 hours ago". */
export function readAge(readAt: number, now: number): string {
  const minutes = Math.floor(Math.max(0, now - readAt) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${String(minutes)} ${minutes === 1 ? "minute" : "minutes"} ago`;
  const hours = Math.floor(minutes / 60);
  return `${String(hours)} ${hours === 1 ? "hour" : "hours"} ago`;
}

/** "Carry over 3 items", "Carry over 1 item". */
export function carryOverLabel(count: number): string {
  return `Carry over ${String(count)} ${count === 1 ? "item" : "items"}`;
}

/** The meeting words for a date: "the agenda for Monday, Nov 9", or the next meeting's without one. */
function agendaFor(date: string | null): string {
  return date ? `the agenda for ${formatMeetingDate(date)}` : "the next meeting's agenda";
}

/**
 * The toast after a carry-over, from the server's answer (its `targetDate` is
 * authoritative). Says how many did not fit when the 20-item cap was reached.
 * Pure; exported for tests.
 */
export function carryOverToast(result: Pick<NextAgendaResult, "added" | "alreadyOnAgenda" | "leftOutFull" | "targetDate">): string {
  const target = agendaFor(result.targetDate);
  const full =
    result.leftOutFull.length > 0
      ? ` · ${String(result.leftOutFull.length)} didn't fit (an agenda holds ${String(MAX_GROOMING_BATCH_ITEMS)})`
      : "";
  if (result.added.length > 0) {
    const items = `${String(result.added.length)} ${result.added.length === 1 ? "item" : "items"}`;
    return `Carried ${items} over to ${target}${full}`;
  }
  if (result.leftOutFull.length > 0) return `Nothing carried over: ${target} is full${full}`;
  if (result.alreadyOnAgenda.length > 0) return `Already on ${target}`;
  return "Nothing to carry over: these items now have points or need fixes first";
}

/**
 * A failed carry-over or undo, in plain words. E323: an agenda whose meeting
 * has passed answers GROOMING_AGENDA_LOCKED. Pure; exported for tests.
 */
export function carryOverError(error: unknown): string {
  switch (groomingApiErrorCode(error)) {
    case "TOOLBOX_CAPABILITY_REQUIRED":
      return "Only Project Managers and Admins can carry items over to the next meeting.";
    case "ADO_PAT_REQUIRED":
    case "ADO_PAT_INVALID":
      return "Save a working Azure DevOps token in Setup, then try again.";
    case "GROOMING_BATCH_FULL":
      return `The next meeting's agenda is full (an agenda holds ${String(MAX_GROOMING_BATCH_ITEMS)}).`;
    case "GROOMING_BATCH_NOT_DRAFT":
      return "The next meeting's agenda is already being prepared, so it can't be changed now.";
    case "GROOMING_AGENDA_LOCKED":
      return GROOMING_AGENDA_ERROR_MESSAGES.GROOMING_AGENDA_LOCKED;
    default:
      return "The items could not be carried over. Try again in a moment.";
  }
}

/** Re-render every half minute so the read's age stays true. */
function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, intervalMs);
    return () => {
      clearInterval(timer);
    };
  }, [intervalMs]);
  return now;
}

/** Type names of items not read live yet, from a backlog read already in the cache; never starts a read. */
function useCachedTypeNames(project: string) {
  const queryClient = useQueryClient();
  const backlogs = queryClient.getQueriesData<{ data: GroomingBacklog }>({
    queryKey: [...groomingKeys.project(project), "backlog"],
  });
  return (itemId: string): string | null => {
    for (const [, response] of backlogs) {
      const row = response?.data.items.find((i) => i.itemId === itemId);
      if (row) return row.typeName;
    }
    return null;
  };
}

const ROW_GRID = "grid grid-cols-[34px_minmax(0,1fr)_150px_120px_136px] items-center gap-4";

const RUN_STATUSES: ReadonlySet<GroomingBatch["status"]> = new Set(["running", "stopped", "completed"]);

/** Claude's size as a compact pill: "5 · range 3–8". */
function ClaudeSize({ item }: { item: GroomingBatchItemView }) {
  const sized = item.brief !== null && (item.status === "briefed" || item.status === "current");
  if (sized && item.brief) {
    const { suggestedPoints, pointsLow, pointsHigh } = item.brief;
    const range = pointsLow === pointsHigh ? String(pointsLow) : `${String(pointsLow)}–${String(pointsHigh)}`;
    return (
      <span
        className="inline-flex h-5 items-center justify-self-start whitespace-nowrap rounded-md border border-dashed border-[#52525B] px-[7px] text-[11px] font-semibold text-[#D4D4D8]"
        data-testid="agenda-claude-size"
      >
        {String(suggestedPoints)} · range {range}
      </span>
    );
  }
  return (
    <span className="flex flex-col gap-1">
      <span
        className={cn(
          "h-5 rounded-md bg-gradient-to-r from-[#111113] via-[#232328] to-[#111113]",
          item.status === "running" && "motion-safe:animate-pulse"
        )}
        aria-hidden="true"
        data-testid="agenda-shimmer"
      />
      <span className={cn("text-xs", TOOLBOX_TEXT.faint)}>{pendingText(item)}</span>
    </span>
  );
}

/** The Team cell: "5 pts" read from ADO, "No points yet", a shimmer while reading, or "Not read". */
function TeamCell({ detail, loading }: { detail: WorkItemDetail | undefined; loading: boolean }) {
  if (!detail) {
    return loading ? (
      <span className="h-5 w-14 rounded-md bg-[#18181B] motion-safe:animate-pulse" aria-label="Reading from Azure DevOps" data-testid="agenda-team" />
    ) : (
      <span className={cn("text-xs", TOOLBOX_TEXT.faint)} data-testid="agenda-team">
        Not read
      </span>
    );
  }
  const points = teamPoints(detail);
  if (points === null) {
    return (
      <span className="justify-self-start" data-testid="agenda-team" data-state="none">
        <Chip tone="warn">No points yet</Chip>
      </span>
    );
  }
  return (
    <span
      className="inline-flex h-5 items-center justify-self-start whitespace-nowrap rounded-md bg-[#27272A] px-[7px] text-[11px] font-semibold text-foreground"
      data-testid="agenda-team"
      data-state="sized"
    >
      {pointsText(points)}
    </span>
  );
}

export interface AgendaViewProps {
  project: string;
  batch: GroomingBatch;
  /** Open an agenda item (by batch item id) in the Workspace. */
  onOpenItem?: (batchItemId: string) => void;
}

export function AgendaView({ project, batch, onOpenItem }: AgendaViewProps) {
  const queryClient = useQueryClient();
  const cachedTypeName = useCachedTypeNames(project);
  // UX-PM-09: each brief's state from the cached whole-backlog read, never a per-row or new backlog read.
  const latestOf = useBacklogBriefs(project);
  const { data: access } = useToolboxAccess();
  const { data: session } = useProjectSession(project);
  const nextAgenda = useNextAgenda(project);
  const undo = useUndoNextAgenda(project);
  const [carryError, setCarryError] = useState<string | null>(null);
  const now = useNow();

  const onAgenda = batch.items.filter((item) => item.status !== "not-ready");
  const notReady = batch.items.filter((item) => item.status === "not-ready");
  const briefed = onAgenda.filter((item) => item.status === "briefed" || item.status === "current").length;
  const toBrief = onAgenda.length - briefed;

  // One live ADO read per agenda item, shared with Present and the shared agenda page (≤ 20, the batch cap).
  const reads = useQueries({
    queries: onAgenda.map((item) => ({
      queryKey: workItemKeys.detail("ado", project, item.itemId),
      queryFn: () => workItemsApi.detail("ado", project, item.itemId),
      select: (response: { data: WorkItemDetail }) => response.data,
      retry: retryTransientOnce,
      staleTime: 60 * 1000,
    })),
  });
  const details = new Map<string, WorkItemDetail>();
  onAgenda.forEach((item, index) => {
    const data = reads[index]?.data;
    if (data) details.set(item.itemId, data);
  });
  const record = meetingRecord(onAgenda, details);
  const readTimes = reads.filter((read) => read.data !== undefined).map((read) => read.dataUpdatedAt);
  const oldestRead = readTimes.length > 0 ? Math.min(...readTimes) : null;
  const reading = reads.some((read) => read.isFetching);
  const failedReads = reads.filter((read) => read.isError && read.data === undefined).length;

  const itemIds = onAgenda.map((item) => item.itemId).join(",");
  const readAgain = useCallback(() => {
    for (const itemId of itemIds ? itemIds.split(",") : []) {
      void queryClient.invalidateQueries({ queryKey: workItemKeys.detail("ado", project, itemId), exact: true });
    }
  }, [queryClient, project, itemIds]);
  // Points are typed in ADO in another window: coming back re-reads every item.
  useRefetchOnReturn(readAgain, onAgenda.length > 0);

  const canCarry = canOn(access, project, "manage-agenda") && batch.mode === "agenda" && RUN_STATUSES.has(batch.status);
  const target = carryOverDate(batch, session?.meetingPattern ?? null);

  const carryOver = () => {
    setCarryError(null);
    nextAgenda.mutate(
      { runId: batch.id, itemIds: record.unsized },
      {
        onSuccess: (result) => {
          const added = result.added;
          const agenda = result.agenda;
          showGroomingToast(
            carryOverToast(result),
            added.length > 0 ? "success" : "info",
            added.length > 0 && agenda
              ? {
                  action: {
                    label: "Undo",
                    onAction: () => {
                      // The toast can outlive this view: a promise, unlike
                      // mutate's per-call callbacks, still settles after an unmount.
                      undo
                        .mutateAsync({ agendaId: agenda.id, itemIds: added, cancelIfEmpty: result.createdAgenda })
                        .then(
                          () => {
                            showGroomingToast("Carry-over undone", "info");
                          },
                          (cause: unknown) => {
                            // E323: past its meeting the agenda is a record, so removing the items there is refused too.
                            showGroomingToast(
                              groomingApiErrorCode(cause) === "GROOMING_AGENDA_LOCKED"
                                ? GROOMING_AGENDA_ERROR_MESSAGES.GROOMING_AGENDA_LOCKED
                                : "The carry-over could not be undone. Remove the items from the next agenda in Session.",
                              "info"
                            );
                          }
                        );
                    },
                  },
                }
              : {}
          );
        },
        onError: (error) => {
          setCarryError(carryOverError(error));
        },
      }
    );
  };

  // The fix list: the run's not-ready items, then items that dropped below the bar since (live reads).
  const notReadyIds = new Set(notReady.map((item) => item.itemId));
  const droppedIds = new Set(record.belowBar);
  const fixes = [
    ...notReady.map((item) => ({ item, reason: item.errorMessage ?? "Missing information" })),
    ...onAgenda
      .filter((item) => droppedIds.has(item.itemId) && !notReadyIds.has(item.itemId))
      .map((item) => ({ item, reason: "No longer meets the grooming bar" })),
  ];

  const readLine =
    oldestRead === null
      ? reading
        ? "Reading the team's points from Azure DevOps…"
        : "The team's points could not be read from Azure DevOps."
      : `Team points read from Azure DevOps ${readAge(oldestRead, now)}.`;

  return (
    <div className="flex flex-col gap-3" data-testid="agenda-view">
      <div className="flex flex-col gap-0.5">
        <h2 className="font-display text-[22px] font-semibold leading-tight tracking-[-0.01em]">{onAgenda.length} on the agenda</h2>
        <p className="text-sm text-muted-foreground" data-testid="agenda-counts">
          {briefed} with a brief · {toBrief} still to brief · {fixes.length} need{fixes.length === 1 ? "s" : ""} fixes first
        </p>
      </div>

      {onAgenda.length > 0 && (
        <section className="glass-card flex flex-wrap items-center gap-4 rounded-[10px] px-[18px] py-3.5" data-testid="agenda-record" aria-label="Recorded in Azure DevOps">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h3 className="font-display text-sm font-semibold" data-testid="agenda-record-headline">
              {oldestRead === null ? "Team points" : recordHeadline(record, onAgenda.length)}
            </h3>
            <p className="text-xs text-muted-foreground" data-testid="agenda-record-read">
              {readLine}
              {failedReads > 0 && oldestRead !== null && ` ${String(failedReads)} could not be read.`}{" "}
              <button
                type="button"
                className="text-link hover:underline disabled:opacity-60"
                onClick={readAgain}
                disabled={reading}
                data-testid="agenda-read-again"
              >
                Read again
              </button>
            </p>
          </div>
          {canCarry && record.unsized.length > 0 && (
            <div className="flex flex-none flex-col items-end gap-1">
              <Button
                type="button"
                size="sm"
                className="border-primary bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={nextAgenda.isPending}
                onClick={carryOver}
                data-testid="agenda-carry-over"
              >
                <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                {carryOverLabel(record.unsized.length)}
              </Button>
              <span className="text-xs text-muted-foreground" data-testid="agenda-carry-over-target">
                to {agendaFor(target)}
              </span>
            </div>
          )}
          {carryError && (
            <p className="w-full text-[13px] text-destructive" role="alert">
              {carryError}
            </p>
          )}
        </section>
      )}

      <div className="glass-card overflow-hidden rounded-[10px]">
        <div
          className={cn(ROW_GRID, "border-b border-[#141417] px-[18px] py-2 text-[11px] font-semibold uppercase tracking-[0.06em]", TOOLBOX_TEXT.faint)}
          aria-hidden="true"
        >
          <span>#</span>
          <span>Item</span>
          <span>Claude</span>
          <span>Team</span>
          <span>Brief</span>
        </div>
        <ol aria-label="Agenda" data-testid="agenda-list">
          {onAgenda.map((item, index) => {
            const state = agendaState(item, latestOf(item.itemId));
            const read = reads[index];
            const detail = details.get(item.itemId);
            const typeName = detail?.typeName ?? cachedTypeName(item.itemId);
            const questions = detail?.groomingBrief?.brief?.body.questions.length ?? null;
            const meta = [
              typeName,
              item.brief ? confidenceWords(item.brief.confidence) : null,
              questions !== null && questions > 0 ? `${String(questions)} ${questions === 1 ? "question" : "questions"}` : null,
            ].filter((part): part is string => Boolean(part));
            const noPoints = detail !== undefined && teamPoints(detail) === null;
            const row = (
              <>
                <span className={cn("font-display text-lg font-semibold", TOOLBOX_TEXT.faint)}>{index + 1}</span>
                <span className="flex min-w-0 flex-col gap-1">
                  <span className="truncate text-sm font-medium">{item.title}</span>
                  <span className="text-xs text-muted-foreground">
                    <span className="font-mono">AB#{item.itemId}</span>
                    {meta.length > 0 ? ` · ${meta.join(" · ")}` : ""}
                  </span>
                </span>
                <ClaudeSize item={item} />
                <TeamCell detail={detail} loading={read?.isLoading ?? false} />
                <span className="justify-self-start">
                  <Chip tone={state.tone} testId="agenda-state">
                    {state.label}
                  </Chip>
                </span>
              </>
            );
            const rowClass = cn(ROW_GRID, "border-b border-[#141417] px-[18px] py-3", noPoints && "bg-[rgba(245,158,11,0.05)]");
            return (
              <li key={item.id} data-testid="agenda-row" data-item-id={item.itemId}>
                {onOpenItem ? (
                  <button
                    type="button"
                    className={cn(rowClass, "w-full text-left transition-colors hover:bg-[#0F0F12]")}
                    onClick={() => {
                      onOpenItem(item.id);
                    }}
                    aria-label={`Open AB#${item.itemId} ${item.title} in the Workspace`}
                  >
                    {row}
                  </button>
                ) : (
                  <div className={rowClass}>{row}</div>
                )}
              </li>
            );
          })}
        </ol>
      </div>

      {fixes.length > 0 && (
        <section className="glass-card overflow-hidden rounded-[10px]" data-testid="fix-before-session" aria-label={FIX_LIST_HEADING}>
          <div className="flex flex-wrap items-center gap-3 border-b border-[#1F1F23] px-[18px] py-2.5">
            <Chip tone="warn">{FIX_LIST_HEADING}</Chip>
            <span className="text-sm text-muted-foreground">These don&apos;t meet the grooming bar yet</span>
          </div>
          <ul>
            {fixes.map(({ item, reason }) => (
              <li
                key={item.id}
                className="flex flex-wrap items-center gap-3 border-b border-[#141417] px-[18px] py-2.5"
                data-testid="fix-row"
                data-item-id={item.itemId}
              >
                <span className="font-mono text-xs text-muted-foreground">AB#{item.itemId}</span>
                <span className="min-w-0 flex-1 truncate text-sm">{item.title}</span>
                <span className="text-sm text-muted-foreground">{reason}</span>
                <Link to={formulatorImprovePath(project, item.itemId)} className="text-sm text-link hover:underline">
                  Fix in ADO Formulator
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
